import React, { useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { getNimbuxCaseSummary } from 'features/common/services';
import { extractErrorMessage } from 'features/quotation/helpers';
import { createSalesOrder, getChannels } from 'features/salesOrder/services';
import { IChannel, SegmentEnum, segmentTypes } from 'features/salesOrder/types';
import { nanoidGenerator } from 'features/common/helpers';
import { LoadedQuotation } from '../../types';
import CaseWhatsappNumberWarning from 'features/common/components/CaseWhatsappNumberWarning';
import { DealSaleOrderConflict, findSaleOrderConflictForDeal } from '../../helpers/dealSaleOrderConflict';

interface Props {
	quotation: LoadedQuotation;
	onOrderCreated: (saleOrderId: string) => void;
	onExistingSaleOrder?: (conflict: DealSaleOrderConflict) => void;
}

interface FormValues {
	crmTicket: string;
	segmentType: string;
	channelId: string;
	clientReference: string;
}

export const StepOrderMetadata = ({ quotation, onOrderCreated, onExistingSaleOrder }: Props): JSX.Element => {
	const [channels, setChannels] = useState<IChannel[]>([]);
	const [loading, setLoading] = useState(false);
	const [caseCheckLoading, setCaseCheckLoading] = useState(false);
	const [caseValid, setCaseValid] = useState<boolean | null>(null);
	const [caseMessage, setCaseMessage] = useState<string | null>(null);
	const [caseClientId, setCaseClientId] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<FormValues>({
		mode: 'onChange',
		defaultValues: {
			crmTicket: String(quotation.crmTicket),
			segmentType: '',
			channelId: '',
			clientReference: '',
		},
	});

	useEffect(() => {
		const load = async () => {
			try {
				const res = await getChannels();
				setChannels(res.data);
			} catch (e) {
				setSubmitError(extractErrorMessage(e, 'Error al cargar canales'));
			}
		};
		void load();
	}, []);

	const validateCase = useCallback(async (crmTicket: number) => {
		if (!crmTicket) return;

		setCaseCheckLoading(true);
		setCaseValid(null);
		setCaseMessage(null);
		setCaseClientId(null);

		try {
			const response = await getNimbuxCaseSummary(crmTicket);
			if (!response.data?.client) {
				setCaseValid(false);
				setCaseMessage('El caso no tiene cliente asociado en Nimbux');
			} else {
				const client = response.data.client;
				if (!client.email && !client.whatsappNumber) {
					setCaseValid(false);
					setCaseMessage('Complete el email o WhatsApp del cliente en el CRM');
				} else {
					if (!client.whatsappNumber) {
						setCaseClientId(client.id);
					}
					setCaseValid(true);
					setCaseMessage(
						`Cliente: ${[client.firstName, client.lastName].filter(Boolean).join(' ')}`.trim() || 'Caso válido',
					);
				}
			}
		} catch (e) {
			setCaseValid(false);
			setCaseMessage(extractErrorMessage(e, 'Error al validar el caso'));
		} finally {
			setCaseCheckLoading(false);
		}
	}, []);

	useEffect(() => {
		void validateCase(quotation.crmTicket);
	}, [quotation.crmTicket, validateCase]);

	const onSubmit = async (values: FormValues) => {
		if (caseValid !== true) {
			setSubmitError('Validá el caso antes de continuar');
			return;
		}

		setLoading(true);
		setSubmitError(null);
		try {
			const conflict = await findSaleOrderConflictForDeal(Number(values.crmTicket), quotation.id);
			if (conflict) {
				onExistingSaleOrder?.(conflict);
				return;
			}

			const saleOrderId = nanoidGenerator();
			await createSalesOrder(saleOrderId, {
				dealId: Number(values.crmTicket),
				isBitrixDeal: false,
				quotationId: quotation.id,
				segmentType: values.segmentType as SegmentEnum,
				channelId: Number(values.channelId),
				clientReference: values.clientReference.trim() || undefined,
			});
			onOrderCreated(saleOrderId);
		} catch (e) {
			setSubmitError(extractErrorMessage(e, 'Error al crear la orden'));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box>
			<Typography variant="h6" fontWeight={700} gutterBottom>
				Datos de la orden
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Caso tomado de la cotización #{quotation.id}. Elegí segmento y canal.
			</Typography>

			<Box component="form" onSubmit={handleSubmit(onSubmit)} display="flex" flexDirection="column" gap={2}>
				<Controller
					name="crmTicket"
					control={control}
					rules={{ required: 'Caso requerido', min: { value: 1, message: 'Debe ser mayor a 0' } }}
					render={({ field }) => (
						<TextField
							{...field}
							label="Caso"
							type="number"
							size="small"
							required
							disabled
							helperText="Desde la cotización"
						/>
					)}
				/>

				{caseCheckLoading && <CircularProgress size={24} />}
				{caseMessage && <Alert severity={caseValid ? 'success' : 'error'}>{caseMessage}</Alert>}
				{caseClientId && (
					<CaseWhatsappNumberWarning clientId={caseClientId} message="Recomendado: WhatsApp del cliente en Nimbux" />
				)}

				<Controller
					name="segmentType"
					control={control}
					rules={{ required: 'Seleccioná segmento' }}
					render={({ field, fieldState }) => (
						<FormControl size="small" fullWidth error={Boolean(fieldState.error)}>
							<InputLabel>Segmento *</InputLabel>
							<Select {...field} label="Segmento *">
								{segmentTypes.map((s) => (
									<MenuItem key={s.id} value={s.id}>
										{s.name}
									</MenuItem>
								))}
							</Select>
							{fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
						</FormControl>
					)}
				/>

				<Controller
					name="channelId"
					control={control}
					rules={{ required: 'Seleccioná canal' }}
					render={({ field, fieldState }) => (
						<FormControl size="small" fullWidth error={Boolean(fieldState.error)}>
							<InputLabel>Canal *</InputLabel>
							<Select {...field} label="Canal *">
								{channels.map((ch) => (
									<MenuItem key={ch.id} value={String(ch.id)}>
										{ch.name}
									</MenuItem>
								))}
							</Select>
							{fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
						</FormControl>
					)}
				/>

				<Controller
					name="clientReference"
					control={control}
					render={({ field }) => <TextField {...field} label="Referencia de cliente" size="small" fullWidth />}
				/>

				{submitError && <Alert severity="error">{submitError}</Alert>}

				<Box display="flex" justifyContent="flex-end">
					<Button
						type="submit"
						variant="contained"
						color="success"
						disabled={loading || caseValid !== true || Boolean(errors.segmentType) || Boolean(errors.channelId)}
					>
						{loading ? <CircularProgress size={22} color="inherit" /> : 'Crear orden y cargar pasajeros'}
					</Button>
				</Box>
			</Box>
		</Box>
	);
};
